'use client'

import React from 'react'
import { useParams, notFound } from 'next/navigation'
import { useTranslations } from '@/components/I18nProvider'
import { motion } from 'framer-motion'
import { Button } from '@sarkiai/ui'
import { Download, ArrowLeft, CheckCircle2, Lightbulb, Target } from 'lucide-react'
import { XLineLogo } from '@/components/ui/XLineLogo'
import { XEduLogo } from '@/components/ui/XEduLogo'
import { XsareLogo } from '@/components/ui/XsareLogo'
import { DemoRequestForm } from '@/components/forms/DemoRequestForm'
import Image from 'next/image'
import Link from 'next/link'

const SERVICES_DATA = {
  xline: {
    logo: XLineLogo,
    pdf: 'mailto:contact@djotta.com?subject=Demande d\'offre XLine',
    image: '/assets/images/services/sea/xline.webp'
  },
  xsare: {
    logo: XsareLogo,
    pdf: '/documents/sea/XSare - Intelligent Hotel System.pdf',
    image: '/assets/images/services/sea/xsare.webp'
  },
  xedu: {
    logo: XEduLogo,
    pdf: '/documents/sea/XEdu - Intelligent Education System.pdf',
    image: '/assets/images/services/sea/xedu.webp'
  }
}

export default function ServicePage() {
  const params = useParams()
  const slug = params.slug as string
  const t = useTranslations('corporate_services')

  const service = SERVICES_DATA[slug as keyof typeof SERVICES_DATA]

  if (!service) {
    notFound()
  }

  const Logo = service.logo

  return (
    <main className="min-h-screen bg-background text-foreground pt-16">
      {/* Split-screen Hero Section */}
      <div className="container mx-auto max-w-6xl px-4 lg:px-12 py-6 md:py-12">
        <div className="grid grid-cols-1 lg:grid-cols-5 gap-12 lg:gap-20 items-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="space-y-10 lg:col-span-2"
          >
            {/* <Link href="/" className="inline-flex items-center gap-2 text-xs text-muted-foreground hover:text-foreground mb-12 transition-colors group">
              <ArrowLeft className="w-3 h-3 group-hover:-translate-x-1 transition-transform" />
              {t('back_to_home') || "Retour à l'accueil"}
            </Link> */}

            <div className="inline-block p-4 rounded-2xl bg-muted/30 border border-border/50">
              <Logo size="lg" showSlogan={false} />
            </div>

            <div className="space-y-6">
              {/* <h1 className="text-4xl md:text-6xl uppercase tracking-tight leading-none">
                {t(`${slug}.title`)}
              </h1> */}
              <p className="text-lg md:text-xl text-muted-foreground font-medium leading-relaxed">
                {t(`${slug}.subtitle`)}
              </p>
            </div>

            <div className="flex flex-wrap gap-4 pt-4">
              <Button asChild size="lg" className="rounded-2xl bg-black text-white dark:bg-white dark:text-black hover:bg-black hover:opacity-90 uppercase tracking-widest text-xs font-bold h-10 px-10 transition-all shadow-xl shadow-black/10 dark:shadow-white/5">
                <a href="#demo">{t('demo_request.submit')}</a>
              </Button>
              <Button asChild variant="outline" size="lg" className="rounded-2xl uppercase tracking-widest text-xs font-bold h-10 px-8 border-foreground/20 text-foreground hover:text-foreground/70 hover:bg-foreground/5 transition-all">
                <a href={service.pdf} download={service.pdf.startsWith('mailto') ? undefined : true} className="flex items-center gap-2">
                  <Download className="w-3 h-3" />
                  {t('download_offer')}
                </a>
              </Button>
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, scale: 0.98 }}
            animate={{ opacity: 1, scale: 1 }}
            className="relative aspect-[1365/881] lg:col-span-3 bg-white rounded-3xl overflow-hidden border border-foreground/20 shadow-2xl"
          >
            <Image
              fill
              priority
              src={service.image}
              alt={t(`${slug}.title`)}
              className="object-contain"
            />
          </motion.div>
        </div>
      </div>

      <div className="container mx-auto max-w-6xl px-4 lg:px-12 py-24 space-y-32">
        {/* Presentation Pillar */}
        <div className="grid grid-cols-1 lg:grid-cols-1 gap-16 items-center">
          <motion.div
            initial={{ opacity: 0, x: -30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            className="space-y-8"
          >
            <div className="space-y-4">
              <div className="flex items-center gap-3 text-foreground uppercase tracking-[0.2em] text-sm font-medium">
                <Lightbulb className="w-5 h-5" />
                <span>{t('presentation_label')}</span>
              </div>
              <h2 className="text-2xl md:text-4xl uppercase tracking-tight leading-none">
                {t('intelligence_in_action')}
              </h2>
            </div>
            <p className="text-lg md:text-xl text-muted-foreground leading-relaxed font-normal italic border-l-2 border-border pl-8 py-2">
              {t(`${slug}.presentation`)}
            </p>
            <div className="grid grid-cols-1 gap-4 pt-4">
              {(t(`${slug}.features_list`, { returnObjects: true }) as unknown as any[]).map((feature, idx) => (
                <div key={idx} className="flex items-center gap-4 text-sm md:text-base  text-foreground">
                  <CheckCircle2 className="w-5 h-5 text-primary shrink-0" />
                  <span>{feature.title}</span>
                </div>
              ))}
            </div>
          </motion.div>
        </div>

        {/* Use Cases Pillar */}
        <div className="space-y-16">
          <div className="text-center space-y-4 max-w-3xl mx-auto">
            <div className="flex items-center justify-center gap-3 text-foreground uppercase tracking-[0.2em] text-sm font-medium">
              <Target className="w-5 h-5" />
              <span>{t('use_cases_label')}</span>
            </div>
            <h2 className="text-4xl md:text-5xl uppercase tracking-tight leading-none">
              {t('real_world_applications')}
            </h2>
            <p className="text-lg text-muted-foreground font-normal">
              {t('use_cases_description')}
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {(t(`${slug}.use_cases`, { returnObjects: true }) as unknown as any[]).map((useCase, idx) => (
              <motion.div
                key={idx}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: idx * 0.1 }}
                className="group p-10 md:p-14 rounded-[3rem] bg-black/[0.02] dark:bg-white/[0.03] border border-black/5 dark:border-white/5 hover:bg-black/[0.04] dark:hover:bg-white/[0.05] transition-all duration-500 relative overflow-hidden"
              >
                <div className="absolute -right-8 -bottom-8 w-32 h-32 bg-primary/5 rounded-full group-hover:scale-150 transition-transform duration-700" />
                <h3 className="text-2xl md:text-3xl font-medium uppercase tracking-tight leading-none mb-6 relative">
                  {useCase.title}
                </h3>
                <p className="text-lg text-muted-foreground leading-relaxed relative font-normal">
                  {useCase.desc}
                </p>
              </motion.div>
            ))}
          </div>
        </div>

        {/* Demo Request Section */}
        <section id="demo" className="relative group">
          <div className="absolute inset-0 bg-primary/5 rounded-[4rem] -rotate-1 scale-105 group-hover:rotate-0 group-hover:scale-100 transition-all duration-700 -z-10" />
          <div className="max-w-3xl mx-auto py-16 px-4">
            <DemoRequestForm serviceName={t(`${slug}.title`)} />
          </div>
        </section>
      </div>
    </main>
  )
}
